/**
 * **The count** (F3): the station's side — who has sat at this booth today and the last five in,
 * each with its Undo.
 */
import { type ReactNode } from 'react';
import { formatTime } from '@cider/ui';
import { hours } from './BoothShared.js';
import { useBooth } from './BoothState.js';

/** The side of the station: who has sat here today and the last five in. */
export function BoothCount(): ReactNode {
  const { today, onNow, undoFromRail } = useBooth();
  return (
    <>
      <div>
        <div className="booth__sidehead">ON AT THIS BOOTH</div>
        <div className="booth__on" data-testid="booth-on">
          {onNow.length
            ? onNow
                .map((o) =>
                  o.until
                    ? `${o.name} ${formatTime(o.since)} – ${formatTime(o.until)}`
                    : `${o.name} since ${formatTime(o.since)}`,
                )
                .join(' · ')
            : 'No one has signed in at this station today.'}
        </div>
      </div>
      <div>
        <div className="booth__sidehead">LAST FIVE IN</div>
        <ul className="booth__list" data-testid="booth-last">
          {today?.lastIn.length ? (
            today.lastIn.map((l) => (
              <li key={`${l.at}${l.name}`}>
                <span className="booth__when">{formatTime(l.at)}</span>
                <span className="booth__lastwho">
                  <strong>{l.name}</strong>
                  <span>
                    {l.area} · {hours(l.startsAt, l.endsAt)}
                  </span>
                </span>
                <button
                  type="button"
                  className="booth__undo"
                  onClick={() => {
                    undoFromRail(l.personId, l.name, l.shiftId);
                  }}
                >
                  Undo
                </button>
              </li>
            ))
          ) : (
            <li className="booth__fine">Nobody yet today.</li>
          )}
        </ul>
      </div>
    </>
  );
}

/** What the guest screen shows, and its three hands. */
export function GuestCard(): ReactNode {
  const { guest, guestHint, holding, setHolding, show, openGuest } = useBooth();
  return (
    <>
      <div className="booth__guestcard">
        <div className="booth__guestcard-k">THE GUEST SCREEN SHOWS</div>
        <div className="booth__guestcard-v" data-testid="booth-guest-says">
          {guest?.on
            ? guest.state === 'welcome'
              ? 'Idle — the welcome'
              : guest.state === 'confirm'
                ? guest.confirmed
                  ? '“Is this you?” — they said yes'
                  : '“Is this you?”'
                : guest.state === 'waiver'
                  ? guest.signed
                    ? 'The waiver — signed'
                    : 'The waiver — waiting for the signature'
                  : guest.state === 'tag'
                    ? 'Done — take your tag from the printer'
                    : guest.state === 'household'
                      ? 'The household — tap who is here'
                      : guest.state === 'whyfamily'
                        ? 'Which family? — they pick theirs'
                        : guest.state === 'wrongday'
                          ? '“You’re on another day — want to help now?”'
                          : guest.state === 'walkup'
                            ? 'A walk-up — finishing their details'
                            : guest.state === 'hold'
                              ? 'Held — nothing changes until you let it go'
                              : 'Thanks — you’re checked out'
            : (guestHint ?? 'The guest screen is off.')}
        </div>
        {guest?.on ? (
          <span className="booth__guestcard-acts">
            <button
              type="button"
              className="booth__guestlink"
              onClick={() => {
                show('welcome');
              }}
            >
              Clear it
            </button>
            <button
              type="button"
              className="booth__guestlink"
              data-testid="booth-hold"
              onClick={() => {
                show(holding ? 'welcome' : 'hold');
                setHolding(!holding);
              }}
            >
              {holding ? 'Let it go' : 'Hold it'}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="booth__guestlink"
            data-testid="booth-guest-button"
            onClick={() => {
              void openGuest();
            }}
          >
            Open it →
          </button>
        )}
      </div>
    </>
  );
}
